/**
 * Utility functions for chat functionality
 */

export interface UserInfo {
  profile?: {
    displayName?: string;
    firstName?: string;
    lastName?: string;
    nickname?: string;
    avatar?: string;
  };
  email?: string;
}

/**
 * Get display name for chat (nickname > displayName > full name > email > Anonymous)
 */
export const getChatDisplayName = (user?: UserInfo | string | null): string => {
  if (!user || typeof user === 'string') {
    return 'Anonymous';
  }

  if (user.profile?.nickname) {
    return user.profile.nickname;
  }

  if (user.profile?.displayName) {
    return user.profile.displayName;
  }

  const firstName = user.profile?.firstName;
  const lastName = user.profile?.lastName;
  if (firstName || lastName) {
    return [firstName, lastName].filter(Boolean).join(' ').trim();
  }

  if (user.email) {
    return user.email.split('@')[0];
  }

  return 'Anonymous';
};

/**
 * Get avatar URL for chat (user avatar > generic avatar)
 */
export const getChatAvatar = (user?: UserInfo | string | null): string => {
  if (!user || typeof user === 'string') {
    return getGenericAvatar('Anonymous');
  }

  if (user.profile?.avatar) {
    return user.profile.avatar;
  }

  return getGenericAvatar(getChatDisplayName(user));
};

/**
 * Generate a generic avatar based on name
 */
export const getGenericAvatar = (name: string): string => {
  const initial = name.charAt(0).toUpperCase();
  const colors = [
    'bg-blue-500',
    'bg-green-500',
    'bg-purple-500',
    'bg-pink-500',
    'bg-yellow-500',
    'bg-indigo-500',
    'bg-red-500',
    'bg-teal-500',
  ];
  const colorIndex = initial.charCodeAt(0) % colors.length;
  return colors[colorIndex];
};

/**
 * Get avatar initials for display
 */
export const getAvatarInitials = (name: string): string => {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.charAt(0).toUpperCase();
};

